"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import RecommendedFeed from "@/components/feed/recommended-feed";
import FollowingFeed from "@/components/feed/following-feed";
import LiveFeed from "@/components/feed/live-feed";
import DiscoverFeed from "@/components/feed/discover-feed-v2";
import { FeedTopBar } from "@/components/feed/feed-top-bar";
import { BrowseSidebar } from "@/components/feed/browse-sidebar";
import { SellHeroSection } from "@/components/feed/sell-hero-section";
import { ShopByCategory } from "@/components/feed/shop-by-category";
import { TrendingRail } from "@/components/feed/trending-rail";
import { DiscoveryRail } from "@/components/feed/discovery-rail";
import { cn } from "@/lib/utils";
import type { FeedPageClientProps } from "@/components/feed/feed-page-client-types";
import { useFeedPageClientState } from "@/components/feed/feed-page-client-state";
import { useOverlay } from "@/components/context/overlay-context";
import { FEED_FOR_YOU_TAB_ENABLED, isFeedScrollableTab } from "@/lib/feed-tab-config";

export default function FeedPageClientDesktop({ isLoggedIn, currentUserId, user }: FeedPageClientProps) {
  const router = useRouter();
  const { activeTab, discoverRefreshKey, filters, handleSearch, handleTabChange, isTransitioning, setFilters } =
    useFeedPageClientState(isLoggedIn);
  const [browseCategory, setBrowseCategory] = useState("all");
  // Spec §E 1.6a: the slider itself lives one level up (feed-page-client.tsx,
  // above the desktop/mobile swap) on the shared overlay panel slot — this
  // client only needs to open it.
  const { openPanel } = useOverlay();

  const scrollableTab = isFeedScrollableTab(activeTab);
  const showHero = activeTab === "live" && browseCategory === "all";

  // BROWSE sidebar / category tiles → results. "All items" returns to Latest
  // in-place; every other category opens its own `/category/<slug>` page, which
  // reuses the feed's filtering scoped (locked) to that category. See
  // `lib/feed/category-slug.ts` for the slug → feed-query mapping.
  const handleBrowseSelect = (id: string) => {
    if (id === "all") {
      setBrowseCategory("all");
      setFilters((prev) => ({ ...prev, categories: [] }));
      handleTabChange("live");
      return;
    }
    router.push(`/category/${id}`);
  };

  const topBar = (
    <FeedTopBar
      activeTab={activeTab}
      onTabChangeAction={handleTabChange}
      isLoggedIn={isLoggedIn}
      onSearchAction={handleSearch}
      currentUserId={currentUserId}
      onFiltersChangeAction={setFilters}
      activeTabForFilters={activeTab}
      snapStyleNav={false}
    />
  );

  // Snap-style tabs (Following / For You) keep their full-bleed immersive layout
  // — no sidebar, no centered column.
  if (!scrollableTab) {
    return (
      <div className="fixed inset-0 flex flex-col">
        {topBar}
        <div className="feed-content-container flex-1 overflow-hidden pt-0" data-feed-scroll-root>
          <div
            className={cn(
              "feed-container mx-auto h-full w-full max-w-none overflow-visible px-0 transition-opacity duration-150",
              isTransitioning ? "opacity-0" : "opacity-100",
            )}
          >
            {FEED_FOR_YOU_TAB_ENABLED && activeTab === "for-you" ? (
              <RecommendedFeed filters={filters} currentUserId={currentUserId} isActive={activeTab === "for-you"} />
            ) : null}
            {activeTab === "following" && <FollowingFeed isLoggedIn={isLoggedIn} currentUserId={currentUserId} />}
          </div>
        </div>
      </div>
    );
  }

  // Browsable tabs (Latest / Discover) → the mockup's 3-column app shell:
  // BROWSE sidebar + centered feed (hero + grid). Right discovery rail is a
  // later slice.
  return (
    <div className="fixed inset-0 flex flex-col">
      {topBar}
      <div className="flex min-h-0 flex-1 pt-(--feed-top-bar-height,4rem)">
        <BrowseSidebar
          activeId={browseCategory}
          onSelectAction={handleBrowseSelect}
          className="hidden lg:block"
        />
        <div
          className="feed-content-container scrollbar-hide flex-1 overflow-y-auto pb-0"
          style={{
            // Pull the center scroller up under the fixed header and pad its
            // content back down, so content scrolls *under* the header and its
            // backdrop-blur/gradient reads the same "frosted" way as mobile
            // (where the feed already scrolls under the top bar). Sidebar + rail
            // stay below the header via the wrapper's pt.
            marginTop: "calc(-1 * var(--feed-top-bar-height, 4rem))",
            paddingTop: "var(--feed-top-bar-height, 4rem)",
          }}
          data-feed-scroll-root
        >
          <div
            className={cn(
              "feed-container mx-auto w-full max-w-[1140px] px-6 py-7 transition-opacity duration-150 sm:px-8 lg:px-10",
              isTransitioning ? "opacity-0" : "opacity-100",
            )}
          >
            {showHero ? (
              <>
                <SellHeroSection user={user ?? null} className="mb-8" />
                <ShopByCategory className="mb-12" onSelectAction={handleBrowseSelect} />
                <TrendingRail
                  className="mb-9"
                  currentUserId={currentUserId}
                  onSeeAll={() => router.push("/trending")}
                />
              </>
            ) : null}
            {activeTab === "live" && <LiveFeed refreshSignal={discoverRefreshKey} currentUserId={currentUserId} />}
            {activeTab === "discover" && (
              <DiscoverFeed
                refreshSignal={discoverRefreshKey}
                filters={filters}
                currentUserId={currentUserId}
                onViewAllCategoryAction={(selection) => openPanel({ kind: "discover-category", selection })}
              />
            )}
          </div>
        </div>
        <DiscoveryRail currentUserId={currentUserId} className="hidden xl:block" />
      </div>
    </div>
  );
}
